import { yupResolver } from '@hookform/resolvers/yup'
import { getData as getCountryList } from 'country-list'
import { map } from 'lodash-es'
import { useCallback, useContext, useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import SelectField from '@/components/select-field'
import {
  FestivalEdition,
  MigrationConfig,
} from '../../../../common/models/dates-settings'
import FormGroup from '../../../../core/components/FormGroup'
import { timezones } from '../../../../core/constants'
import {
  formatDateTime,
  parseFormDateTime,
} from '../../../../core/constants/times'
import { EditionChange } from '../../../../core/context/EditionChangeContext'
import {
  createEditions,
  deleteFestivalEdition,
  getEditions,
  publishFestivalEdition,
  updateEditionSingelEdit,
} from '../../../../core/services/festival-settings'
import { DeleteConfirmationModal } from '../../DeleteConfirmationModal'
import { MessageBox } from '../../messages/MessageBox'
import { Badge, Button, Card, Checkbox, Input } from '@/components/ui'
import { DateTimePicker } from '@/components/ui/date-time-picker'
import { Icon } from '@/common/components/Icon'
import { cn } from '@/lib/utils'

interface IEditionEditValues {
  eId: string
  name: string
  active: boolean
  backstageEventCode: string
  startDate: string
  endDate: string
  organizerAddress: string
  enableWeb3EventsIntegration: boolean
  locationEnabled: boolean
  country?: string
  city?: string
  localTime?: string
  latitude?: string
  longitude?: string
}

interface IEditionsValues {
  editions: IEditionEditValues[]
}

const toEditionEditValues = (edition: FestivalEdition): IEditionEditValues => {
  const start = new Date(edition.startDate)
  const end = new Date(edition.endDate)
  return {
    eId: edition.id,
    name: edition.name,
    backstageEventCode: edition.backstageEventCode,
    active: edition.active,
    startDate: Number.isNaN(start.getTime())
      ? ''
      : formatDateTime(start.toISOString()),
    endDate: Number.isNaN(end.getTime())
      ? ''
      : formatDateTime(end.toISOString()),
    organizerAddress: edition.organizerAddress ?? '',
    enableWeb3EventsIntegration: edition.enableWeb3EventsIntegration ?? false,
    locationEnabled: Boolean(edition.country || edition.city),
    country: edition.country ?? '',
    city: edition.city ?? '',
    localTime: edition.localTime ?? '',
    latitude: edition.latitude ?? '',
    longitude: edition.longitude ?? '',
  }
}

export interface IEditionsValuesCreate {
  name: string
  migrationConfig: MigrationConfig
  autoActivate: boolean
  activateDate?: string | null
  startDate: string
  endDate: string
  backstageEventCode: string
}

export const Editions: React.FC<{ hasEditPermission: boolean }> = ({
  hasEditPermission,
}) => {
  const { editionId, festivalName } = useParams()

  const [editions, setEditions] = useState<FestivalEdition[]>()
  const [openIndex, setOpenIndex] = useState<string>()
  const { setEditionChanged } = useContext(EditionChange)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [deleteId, setDeleteId] = useState<string>()
  const [addNewEdition, setAddNewEdition] = useState<boolean>(false)
  const [isSubmittingEdit, setIsSubmittingEdit] = useState<boolean>(false)
  const [isSubmittingCreate, setIsSubmittingCreate] = useState<boolean>(false)

  const getData = useCallback(async () => {
    // prettier-ignore
    (async () => {
      try {
        const resp = await getEditions()
        setEditions(resp.data)
        setTimeout(() => {
          setValueEdit(`editions`, map(resp.data, toEditionEditValues))
        }, 200)

      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  useEffect(() => {
    getData()
  }, [getData])

  const schemaEdit = yup.object().shape({
    editions: yup
      .array()
      .of(
        yup.object().shape({
          eId: yup.string().required(),
          name: yup.string().required(),
          active: yup.boolean().required(),
          startDate: yup.string().required(),
          endDate: yup.string().required(),
          backstageEventCode: yup.string().required(),
          organizerAddress: yup.string().default(''),
          enableWeb3EventsIntegration: yup.boolean().required(),
          locationEnabled: yup.boolean().required(),
          country: yup.string().when('locationEnabled', {
            is: true,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional().default(''),
          }),
          city: yup.string().when('locationEnabled', {
            is: true,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional().default(''),
          }),
          localTime: yup.string().when('locationEnabled', {
            is: true,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional().default(''),
          }),
          latitude: yup.string().when('locationEnabled', {
            is: true,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional().default(''),
          }),
          longitude: yup.string().when('locationEnabled', {
            is: true,
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional().default(''),
          }),
        }),
      )
      .required(),
  })

  const {
    register: registerEdit,
    formState: { errors: errorsEdit },
    setValue: setValueEdit,
    trigger: triggerEddit,
    watch: watchEdit,
    control: controlEdit,
  } = useForm<IEditionsValues>({
    mode: 'onChange',
    resolver: yupResolver<IEditionsValues>(schemaEdit),
    defaultValues: {},
  })

  const { fields: editionEditFields } = useFieldArray({
    control: controlEdit,
    name: 'editions',
  })

  const submitEdit = async (index: number) => {
    const isValid = await triggerEddit(`editions.${index}`)
    if (!isValid) return
    const edit = watchEdit(`editions.${index}`)

    setIsSubmittingEdit(true)
    try {
      if (edit) {
        const data: FestivalEdition = {
          id: edit.eId,
          name: edit.name,
          startDate: new Date(edit.startDate).valueOf(),
          endDate: new Date(edit.endDate).valueOf(),
          active: edit.active,
          backstageEventCode: edit.backstageEventCode,
          organizerAddress: edit.organizerAddress,
          enableWeb3EventsIntegration: edit.enableWeb3EventsIntegration,
          country: edit.country ?? '',
          city: edit.city ?? '',
          localTime: edit.localTime ?? '',
          latitude: edit.latitude ?? '',
          longitude: edit.longitude ?? '',
        }
        const { status } = await updateEditionSingelEdit(edit.eId, data)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setIsSubmittingEdit(false)
    }
  }

  const handlePublish = async (id: string) => {
    try {
      const resp = await publishFestivalEdition(id)
      if (resp.status === 200) {
        getData()
        setEditionChanged(true)
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Publish Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const deleteFunc = async () => {
    try {
      if (!deleteId) return
      const { status } = await deleteFestivalEdition(deleteId)
      getData()
      if (status === 204) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Delete Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleCancel = (id: string) => {
    const temp = map(editions, (edition, index) => {
      if (edition.id === id) {
        return toEditionEditValues(edition)
      }
      return editionEditFields?.[index]
    }).filter(Boolean) as IEditionEditValues[]
    setValueEdit(`editions`, temp)
    setOpenIndex('')
  }

  const schemaCreate = yup.object().shape({
    name: yup.string().required(),
    migrationConfig: yup.object().shape({
      migrateInfos: yup.boolean().required().default(false),
      migratePerformances: yup.boolean().required().default(false),
      migratePartners: yup.boolean().required().default(false),
      migrateTicketCategories: yup.boolean().required().default(false),
      migrateExtras: yup.boolean().required().default(false),
      migrateVideos: yup.boolean().required().default(false),
      migrateImages: yup.boolean().required().default(false),
    }),
    autoActivate: yup.boolean().required(),
    activateDate: yup.string().when('autoActivate', {
      is: (val: string) => (val ? true : false),
      then: (schema) => schema.optional().nullable(),
      otherwise: (schema) => schema.required(),
    }),
    startDate: yup.string().required(),
    endDate: yup.string().required(),
    backstageEventCode: yup.string().required(),
  })

  const {
    handleSubmit: handleSubmitCreate,
    register: registerCreate,
    control: controlCreate,
    formState: { errors: errorsCreate },
    watch: watchCreate,
    reset: resetCreate,
    setValue: setValueCreate,
  } = useForm<IEditionsValuesCreate>({
    mode: 'onChange',
    resolver: yupResolver<IEditionsValuesCreate>(schemaCreate),
    defaultValues: {
      migrationConfig: {
        migrateInfos: true,
        migratePerformances: true,
        migratePartners: true,
        migrateTicketCategories: true,
        migrateExtras: true,
        migrateVideos: true,
        migrateImages: true,
      },
      autoActivate: true,
    },
  })

  const watchAutoActivate = watchCreate('autoActivate')

  const submitCreate = async (values: IEditionsValuesCreate) => {
    setIsSubmittingCreate(true)
    try {
      const data = {
        editionName: values.name,
        migrationConfig: values.migrationConfig,
        autoActivate: values.autoActivate,
        activateDate:
          values.autoActivate || !values.activateDate
            ? null
            : new Date(values.activateDate).valueOf(),
        startDate: new Date(values.startDate).valueOf(),
        endDate: new Date(values.endDate).valueOf(),
        backstageEventCode: values.backstageEventCode,
      }
      const { status } = await createEditions(data)
      getData()
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Create Successful')
        resetCreate()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setIsSubmittingCreate(false)
    }
  }

  return (
    <>
      <Card className="border border-monochrome-120 bg-gradient-4 p-7.5">
        <h3 className="text-title-xs text-monochrome-1 mb-6">Editions</h3>
        <div className="flex flex-col space-y-4">
          {map(editionEditFields, (edition, index) => {
            return (
              <form id="settings-editions-edit-form" key={edition.id}>
                <div className="flex gap-2 w-full items-center justify-between border-t border-monochrome-120 pt-4">
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'text-title-xs text-monochrome-1',
                        openIndex === edition.eId ? 'text-accent-1' : '',
                      )}
                    >
                      {edition.name}
                    </span>
                    {edition.active && (
                      <Badge
                        variant="secondary"
                        className="border border-monochrome-120"
                      >
                        current
                      </Badge>
                    )}
                  </div>
                  <Button
                    onClick={() => {
                      if (openIndex === edition.eId) {
                        setOpenIndex(undefined)
                      } else {
                        setOpenIndex(edition.eId)
                      }
                    }}
                    variant="secondary"
                    type="button"
                    className="w-[82px]"
                  >
                    <Icon
                      icon="chevron-down"
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      className={cn(
                        'transition-transform duration-300',
                        openIndex === edition.eId ? 'rotate-180' : '',
                      )}
                    />
                  </Button>
                </div>
                {openIndex === edition.eId && (
                  <div>
                    <div className="flex flex-col gap-3 my-4">
                      <div className="grid grid-cols-4 gap-3">
                        <FormGroup label="Edition Name">
                          <Input
                            isInvalid={
                              !!errorsEdit?.editions?.[index]?.name?.message
                            }
                            {...registerEdit(`editions.${index}.name`)}
                            type="text"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                        <FormGroup label="Backstage Event Code">
                          <Input
                            isInvalid={
                              !!errorsEdit?.editions?.[index]
                                ?.backstageEventCode?.message
                            }
                            {...registerEdit(
                              `editions.${index}.backstageEventCode`,
                            )}
                            type="text"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>

                        <FormGroup label="Starting Date">
                          <DateTimePicker
                            className="h-[44px] rounded-2xl"
                            value={parseFormDateTime(
                              watchEdit(`editions.${index}.startDate`),
                            )}
                            onChange={(date) => {
                              setValueEdit(
                                `editions.${index}.startDate`,
                                date ? formatDateTime(date.toISOString()) : '',
                              )
                            }}
                            hasError={
                              !!errorsEdit?.editions?.[index]?.startDate
                                ?.message
                            }
                            disabled={!hasEditPermission}
                            maxDate={parseFormDateTime(
                              watchEdit(`editions.${index}.endDate`),
                            )}
                          />
                        </FormGroup>
                        <FormGroup label="End Date">
                          <DateTimePicker
                            className="h-[44px] rounded-2xl"
                            value={parseFormDateTime(
                              watchEdit(`editions.${index}.endDate`),
                            )}
                            onChange={(date) => {
                              setValueEdit(
                                `editions.${index}.endDate`,
                                date ? formatDateTime(date.toISOString()) : '',
                              )
                            }}
                            hasError={
                              !!errorsEdit?.editions?.[index]?.endDate?.message
                            }
                            disabled={!hasEditPermission}
                            minDate={parseFormDateTime(
                              watchEdit(`editions.${index}.startDate`),
                            )}
                          />
                        </FormGroup>
                      </div>
                      <FormGroup
                        label="Location"
                        direction="row"
                        labelClassName="whitespace-nowrap"
                        inputWrapperClassName="w-auto"
                        wrapperClassName="w-auto flex-row-reverse justify-end"
                        labelProps={{
                          htmlFor: `locationEnabled-${index}`,
                        }}
                      >
                        <Checkbox
                          id={`locationEnabled-${index}`}
                          checked={watchEdit(
                            `editions.${index}.locationEnabled`,
                          )}
                          onCheckedChange={(checked) => {
                            setValueEdit(
                              `editions.${index}.locationEnabled`,
                              checked === 'indeterminate' ? false : checked,
                            )
                          }}
                          disabled={!hasEditPermission}
                        />
                      </FormGroup>
                      <div className="grid grid-cols-3 gap-3">
                        {watchEdit(`editions.${index}.locationEnabled`) && (
                          <>
                            <FormGroup label="Country">
                              <SelectField
                                outline
                                placeholder="Select Country"
                                options={map(getCountryList(), (country) => ({
                                  label: country.name,
                                  value: country.name,
                                }))}
                                triggerClassName="!h-[44px] rounded-[16px]"
                                value={watchEdit(`editions.${index}.country`)}
                                onValueChange={(value) => {
                                  setValueEdit(
                                    `editions.${index}.country`,
                                    value,
                                  )
                                }}
                                disabled={!hasEditPermission}
                              />
                            </FormGroup>
                            <FormGroup label="City">
                              <Input
                                isInvalid={
                                  !!errorsEdit?.editions?.[index]?.city?.message
                                }
                                {...registerEdit(`editions.${index}.city`)}
                                type="text"
                                disabled={!hasEditPermission}
                              />
                            </FormGroup>
                          </>
                        )}
                        <FormGroup label="Time zone Festival">
                          <SelectField
                            placeholder="Select Timezone"
                            outline
                            options={map(timezones, (timezone) => ({
                              label: timezone.name,
                              value: timezone.value,
                            }))}
                            triggerClassName="!h-[44px] rounded-[16px]"
                            value={watchEdit(`editions.${index}.localTime`)}
                            onValueChange={(value) => {
                              setValueEdit(`editions.${index}.localTime`, value)
                            }}
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <FormGroup label="Latitude">
                          <Input
                            isInvalid={
                              !!errorsEdit?.editions?.[index]?.latitude?.message
                            }
                            {...registerEdit(`editions.${index}.latitude`)}
                            type="text"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                        <FormGroup label="Longitude">
                          <Input
                            isInvalid={
                              !!errorsEdit?.editions?.[index]?.longitude
                                ?.message
                            }
                            {...registerEdit(`editions.${index}.longitude`)}
                            type="text"
                            disabled={!hasEditPermission}
                          />
                        </FormGroup>
                      </div>
                    </div>
                    {edition.active ? (
                      <div className="flex justify-end gap-2 border-t border-monochrome-120 pt-4 [&>*]:w-[154px]">
                        <Button
                          onClick={() => {
                            handleCancel(edition.eId)
                          }}
                          variant="secondary"
                          type="button"
                          disabled={!hasEditPermission}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="primary"
                          form="settings-editions-edit-form"
                          onClick={() => {
                            submitEdit(index)
                          }}
                          disabled={isSubmittingEdit || !hasEditPermission}
                          loading={isSubmittingEdit}
                        >
                          Save
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-end gap-2 border-t border-monochrome-120 pt-4">
                        <Button
                          variant="secondary"
                          type="button"
                          className="w-[154px]"
                          onClick={() => handlePublish(edition.eId)}
                          disabled={!hasEditPermission}
                        >
                          Publish
                        </Button>
                        <Button
                          variant="destructive"
                          className="w-[116px]"
                          type="button"
                          onClick={() => {
                            setDeleteId(edition.eId)
                            setDeleteConfirmationModal(true)
                          }}
                          disabled={!hasEditPermission}
                        >
                          Delete
                          <Icon
                            icon="close-circle"
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            className="shrink-0"
                          />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </form>
            )
          })}
          {!addNewEdition && (
            <div className="flex justify-center border-t border-monochrome-120 pt-4">
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  setOpenIndex(undefined)
                  setAddNewEdition(true)
                }}
              >
                Add new Edition
                <Icon
                  icon="plus"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  className="shrink-0"
                />
              </Button>
            </div>
          )}
          {addNewEdition && (
            <form
              id="settings-editions-create-form"
              className="border-t border-monochrome-120 pt-4"
              onSubmit={handleSubmitCreate(submitCreate)}
            >
              <h3 className="text-title-xs text-monochrome-1 mb-4">
                Add New Edition
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <FormGroup label="Next Edition">
                  <Input
                    isInvalid={!!errorsCreate?.name?.message}
                    placeholder="Enter Edition Name"
                    {...registerCreate(`name`)}
                    type="text"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>

                <FormGroup label="Starting Date">
                  <DateTimePicker
                    className="h-[44px] rounded-2xl"
                    value={parseFormDateTime(watchCreate('startDate'))}
                    onChange={(date) => {
                      setValueCreate(
                        'startDate',
                        date ? formatDateTime(date.toISOString()) : '',
                      )
                    }}
                    hasError={!!errorsCreate?.startDate?.message}
                    disabled={!hasEditPermission}
                    maxDate={parseFormDateTime(watchCreate('endDate'))}
                  />
                </FormGroup>
                <FormGroup label="End Date">
                  <DateTimePicker
                    className="h-[44px] rounded-2xl"
                    value={parseFormDateTime(watchCreate('endDate'))}
                    onChange={(date) => {
                      setValueCreate(
                        'endDate',
                        date ? formatDateTime(date.toISOString()) : '',
                      )
                    }}
                    hasError={!!errorsCreate?.endDate?.message}
                    disabled={!hasEditPermission}
                    minDate={parseFormDateTime(watchCreate('startDate'))}
                  />
                </FormGroup>

                <FormGroup label="Publish Date">
                  <DateTimePicker
                    className="h-[44px] rounded-2xl"
                    value={parseFormDateTime(
                      watchCreate('activateDate') as string,
                    )}
                    onChange={(date) => {
                      setValueCreate(
                        'activateDate',
                        date ? formatDateTime(date.toISOString()) : null,
                      )
                    }}
                    placeholder="Enter Publish Date"
                    hasError={!!errorsCreate?.activateDate?.message}
                    disabled={watchAutoActivate || !hasEditPermission}
                  />
                </FormGroup>
                <FormGroup label="Backstage Event Code">
                  <Input
                    isInvalid={!!errorsCreate?.backstageEventCode?.message}
                    {...registerCreate(`backstageEventCode`)}
                    type="text"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
                <FormGroup
                  label="Auto Activate"
                  direction="row"
                  labelClassName="whitespace-nowrap"
                  inputWrapperClassName="w-auto"
                  wrapperClassName="w-auto flex-row-reverse justify-end mt-4"
                >
                  <Controller
                    name="autoActivate"
                    control={controlCreate}
                    render={({ field: { onChange, value, ref } }) => (
                      <Checkbox
                        id="autoActivate"
                        isInvalid={!!errorsCreate?.autoActivate?.message}
                        ref={ref}
                        checked={value}
                        onCheckedChange={(checked) =>
                          onChange(Boolean(checked))
                        }
                        disabled={!hasEditPermission}
                        className="border-monochrome-120"
                      />
                    )}
                  />
                </FormGroup>
                <FormGroup
                  label="What to migrate"
                  labelClassName="text-title-xs"
                  wrapperClassName="col-span-full"
                >
                  <div className="grid grid-cols-4 w-full gap-x-[25px] gap-y-3 divide-x divide-monochrome-120 mt-4">
                    <FormGroup
                      label="Info Page"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="w-auto flex-row-reverse justify-end"
                      labelProps={{
                        htmlFor: 'migrationConfig.migrateInfos',
                      }}
                    >
                      <Controller
                        name="migrationConfig.migrateInfos"
                        control={controlCreate}
                        render={({ field: { onChange, value, ref } }) => (
                          <Checkbox
                            id="migrationConfig.migrateInfos"
                            isInvalid={
                              !!errorsCreate?.migrationConfig?.migrateInfos
                                ?.message
                            }
                            ref={ref}
                            checked={value}
                            onCheckedChange={(checked) =>
                              onChange(Boolean(checked))
                            }
                            disabled={!hasEditPermission}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Stages (performances)"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="w-auto flex-row-reverse justify-end"
                      labelProps={{
                        htmlFor: 'migrationConfig.migratePerformances',
                      }}
                    >
                      <Controller
                        name="migrationConfig.migratePerformances"
                        control={controlCreate}
                        render={({ field: { onChange, value, ref } }) => (
                          <Checkbox
                            id="migrationConfig.migratePerformances"
                            isInvalid={
                              !!errorsCreate?.migrationConfig
                                ?.migratePerformances?.message
                            }
                            ref={ref}
                            checked={value}
                            onCheckedChange={(checked) =>
                              onChange(Boolean(checked))
                            }
                            disabled={!hasEditPermission}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Partners"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="w-auto flex-row-reverse justify-end"
                      labelProps={{
                        htmlFor: 'migrationConfig.migratePartners',
                      }}
                    >
                      <Controller
                        name="migrationConfig.migratePartners"
                        control={controlCreate}
                        render={({ field: { onChange, value, ref } }) => (
                          <Checkbox
                            id="migrationConfig.migratePartners"
                            isInvalid={
                              !!errorsCreate?.migrationConfig?.migratePartners
                                ?.message
                            }
                            ref={ref}
                            checked={value}
                            onCheckedChange={(checked) =>
                              onChange(Boolean(checked))
                            }
                            disabled={!hasEditPermission}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Ticket Categories"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="w-auto flex-row-reverse justify-end"
                      labelProps={{
                        htmlFor: 'migrationConfig.migrateTicketCategories',
                      }}
                    >
                      <Controller
                        name="migrationConfig.migrateTicketCategories"
                        control={controlCreate}
                        render={({ field: { onChange, value, ref } }) => (
                          <Checkbox
                            id="migrationConfig.migrateTicketCategories"
                            isInvalid={
                              !!errorsCreate?.migrationConfig
                                ?.migrateTicketCategories?.message
                            }
                            ref={ref}
                            checked={value}
                            onCheckedChange={(checked) =>
                              onChange(Boolean(checked))
                            }
                            disabled={!hasEditPermission}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Add-Ons"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="w-auto flex-row-reverse justify-end"
                      labelProps={{
                        htmlFor: 'migrationConfig.migrateExtras',
                      }}
                    >
                      <Controller
                        name="migrationConfig.migrateExtras"
                        control={controlCreate}
                        render={({ field: { onChange, value, ref } }) => (
                          <Checkbox
                            id="migrationConfig.migrateExtras"
                            isInvalid={
                              !!errorsCreate?.migrationConfig?.migrateExtras
                                ?.message
                            }
                            ref={ref}
                            checked={value}
                            onCheckedChange={(checked) =>
                              onChange(Boolean(checked))
                            }
                            disabled={!hasEditPermission}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Videos"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="w-auto flex-row-reverse justify-end"
                      labelProps={{
                        htmlFor: 'migrationConfig.migrateVideos',
                      }}
                    >
                      <Controller
                        name="migrationConfig.migrateVideos"
                        control={controlCreate}
                        render={({ field: { onChange, value, ref } }) => (
                          <Checkbox
                            id="migrationConfig.migrateVideos"
                            isInvalid={
                              !!errorsCreate?.migrationConfig?.migrateVideos
                                ?.message
                            }
                            ref={ref}
                            checked={value}
                            onCheckedChange={(checked) =>
                              onChange(Boolean(checked))
                            }
                            disabled={!hasEditPermission}
                          />
                        )}
                      />
                    </FormGroup>
                    <FormGroup
                      label="Images"
                      direction="row"
                      labelClassName="whitespace-nowrap"
                      inputWrapperClassName="w-auto"
                      wrapperClassName="w-auto flex-row-reverse justify-end"
                      labelProps={{
                        htmlFor: 'migrationConfig.migrateImages',
                      }}
                    >
                      <Controller
                        name="migrationConfig.migrateImages"
                        control={controlCreate}
                        render={({ field: { onChange, value, ref } }) => (
                          <Checkbox
                            id="migrationConfig.migrateImages"
                            isInvalid={
                              !!errorsCreate?.migrationConfig?.migrateImages
                                ?.message
                            }
                            ref={ref}
                            checked={value}
                            onCheckedChange={(checked) =>
                              onChange(Boolean(checked))
                            }
                            disabled={!hasEditPermission}
                          />
                        )}
                      />
                    </FormGroup>
                  </div>
                </FormGroup>
                <div className="col-span-full flex justify-end gap-2 border-b pt-4 border-monochrome-120 pb-4 [&>*]:w-[154px]">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      setAddNewEdition(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    form="settings-editions-create-form"
                    type="submit"
                    disabled={isSubmittingCreate || !hasEditPermission}
                    loading={isSubmittingCreate}
                  >
                    Create
                  </Button>
                </div>
              </div>
            </form>
          )}
        </div>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Edition'}
        deleteFunc={deleteFunc}
      />
    </>
  )
}
